// src/pages/admin/AdminCertificados.js
import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import AdminLayout from "../../components/admin/AdminLayout";
import { useParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "https://crm-backend-5tk6.onrender.com";

const TopRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 9px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  width: 260px;
  &:focus { outline: none; border-color: #c8102e; }
`;

const Btn = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.danger ? "#f44336" : p.$secondary ? "#fff" : "#c8102e"};
  color: ${p => p.$secondary ? "#444" : "#fff"};
  border: ${p => p.$secondary ? "1px solid #ddd" : "none"};
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  font-size: 0.83rem;
`;

const Th = styled.th`
  background: #f9f9f9;
  padding: 11px 14px;
  text-align: left;
  color: #555;
  font-weight: 600;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 2px 6px;
  border-radius: 4px;
  &:hover { background: #f0f0f0; }
`;

const Pagination = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 16px;
  font-size: 0.85rem;
  color: #666;
`;

const Modal = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  width: 520px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  h3 { margin: 0 0 20px; color: #333; }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  label { font-size: 0.8rem; color: #555; display: block; margin-bottom: 4px; }
  input { width: 100%; padding: 8px 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 0.85rem; box-sizing: border-box;
    &:focus { outline: none; border-color: #c8102e; } }
`;

const ModalFooter = styled.div`
  display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px;
`;

const Empty = styled.p`color: #aaa; text-align: center; padding: 32px; font-size: 0.9rem;`;

export default function AdminCertificados() {
  const { tipo } = useParams();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // null | {modo:'crear'|'editar', registro:{}}
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const limite = 20;

  const token = localStorage.getItem("admin_token");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pagina, limite, buscar });
      const res = await fetch(`${API_URL}/api/admin/certificados/${tipo}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setData(json.data || []);
      setTotal(json.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tipo, pagina, buscar, token]);

  useEffect(() => { setPagina(1); }, [tipo, buscar]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const abrirCrear = () => { setForm({}); setModal({ modo: "crear" }); };
  const abrirEditar = (reg) => { setForm({ ...reg }); setModal({ modo: "editar", registro: reg }); };
  const cerrarModal = () => { setModal(null); setForm({}); };

  const pkField = ["CEC", "CENCAD"].includes(tipo?.toUpperCase()) ? "id" : "folio";

  const guardar = async () => {
    setSaving(true);
    try {
      const esEditar = modal.modo === "editar";
      const url = esEditar
        ? `${API_URL}/api/admin/certificados/${tipo}/${form[pkField]}`
        : `${API_URL}/api/admin/certificados/${tipo}`;
      const res = await fetch(url, {
        method: esEditar ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Error al guardar.");
      cerrarModal();
      fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (reg) => {
    if (!window.confirm(`¿Eliminar ${reg[pkField]}?`)) return;
    try {
      await fetch(`${API_URL}/api/admin/certificados/${tipo}/${reg[pkField]}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (err) {
      alert("Error al eliminar.");
    }
  };

  const formatearValor = (campo, valor) => {
  if (!valor) return "—";
  // Detectar campos de fecha por nombre
  if (["fecha","vigencia","fecha_emision","fecha_inicio","fecha_fin","created_at"].includes(campo)) {
    const d = new Date(valor);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
    }
  }
  return String(valor).substring(0, 30);
};

  const columnas = data.length ? Object.keys(data[0]) : [];
  const totalPags = Math.ceil(total / limite);

  return (
    <AdminLayout titulo={`Certificados — ${tipo}`}>
      <TopRow>
        <SearchInput
          placeholder="Buscar por folio o CURP..."
          value={buscar}
          onChange={e => setBuscar(e.target.value)}
        />
        <Btn onClick={abrirCrear}>+ Nuevo registro</Btn>
        <span style={{ marginLeft: "auto", color: "#888", fontSize: "0.85rem" }}>
          {total.toLocaleString()} registros
        </span>
      </TopRow>

      {loading ? (
        <Empty>Cargando...</Empty>
      ) : data.length === 0 ? (
        <Empty>Sin resultados.</Empty>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table>
            <thead>
              <tr>
                {columnas.map(c => <Th key={c}>{c}</Th>)}
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((reg, i) => (
                <tr key={i}>
                  {columnas.map(c => <Td key={c} title={reg[c]}>{reg[c] !== null ? formatearValor(c, reg[c]) : "—"}</Td>)}
                  <Td>
                    <ActionBtn title="Editar" onClick={() => abrirEditar(reg)}>✏️</ActionBtn>
                    <ActionBtn title="Eliminar" onClick={() => eliminar(reg)}>🗑️</ActionBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Pagination>
        <Btn $secondary disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>← Anterior</Btn>
        <span>Página {pagina} de {totalPags || 1}</span>
        <Btn $secondary disabled={pagina >= totalPags} onClick={() => setPagina(p => p + 1)}>Siguiente →</Btn>
      </Pagination>

      {modal && (
        <Modal onClick={e => e.target === e.currentTarget && cerrarModal()}>
          <ModalBox>
            <h3>{modal.modo === "crear" ? "Nuevo registro" : "Editar registro"} — {tipo}</h3>
            <FormGrid>
              {(modal.modo === "editar" ? columnas : columnas.length ? columnas : Object.keys(form)).map(campo => (
                <Field key={campo}>
                  <label>{campo}</label>
                  <input
                    value={form[campo] !== undefined ? form[campo] : ""}
                    onChange={e => setForm(f => ({ ...f, [campo]: e.target.value }))}
                    disabled={campo === pkField && modal.modo === "editar"}
                  />
                </Field>
              ))}
            </FormGrid>
            <ModalFooter>
              <Btn $secondary onClick={cerrarModal}>Cancelar</Btn>
              <Btn onClick={guardar} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Btn>
            </ModalFooter>
          </ModalBox>
        </Modal>
      )}
    </AdminLayout>
  );
}